<template lang="pug">
    div
        .form__title
            slot(name='title')
        form.form__body(action='javascript://', v-if='params')
            template(v-if="gameMode !== 'generator-settings'")
                .form__field
                    label.form__label
                        | {{ $i18n.t('trainer.trainer_gameFormSetting_tableSize') }}
                    .form__field-body
                        cs-multiselect(
                            :multiple="false",
                            :options="keenEyeConfig.difficulties",
                            label="name",
                            track-by="name",
                            :allow-empty="false",
                            v-model="params.difficult",
                            :placeholder="params.difficult.name",
                        )
                .form__field
                    label.form__label
                        | {{ $i18n.t('progress.progress_preview_timeLimit') }}
                    .form__field-body
                        cs-multiselect(
                            v-model="params.timeLimit"
                            :options="minutes"
                            :placeholder="timeLimitLabel(params.timeLimit)"
                            :multiple="false"
                            @select='selectTimeLimit($event)'
                            :custom-label="timeLimitLabel"
                        )
                .form__field
                    label.form__label
                        | {{ $i18n.t('trainer.trainer_gameFormSetting_chooseColor') }}
                    .form__field-body
                        cs-multiselect(
                            :show-labels="false"
                            :close-on-select="false"
                            :clear-on-select="false"
                            :hide-selected="true"
                            v-model="params.selectedColors"
                            :options="keenEyeConfig.colors"
                            track-by="value"
                            placeholder=""
                            :custom-label="colorLabel"
                            :multiple="true"
                            :max="keenEyeConfig.maxAmountOfColors",
                        )
                            template(#maxElements)
                                | {{ $i18n.t('trainer.trainer_gameFormSetting_maxAmountOfColors') }}
                .form__field
                    label.form__label
                        | {{ $i18n.t('trainer.trainer_gameFormSetting_displaySpeed') }}
                    .form__field-body
                        cs-multiselect(
                            v-model="params.displaySpeed"
                            :options="seconds"
                            :placeholder="displaySpeedLabel(params.displaySpeed)"
                            :multiple="false"
                            @select='selectDisplaySpeed($event)'
                            :custom-label="displaySpeedLabel",
                        )
                divider
            slot(name='parameters')
            slot(name='help')
            .form__buttons(v-if='!usersIds')
                cs-button.form__button(@click='cancel', color="blue", pattern="primary-outline")
                    | {{ $i18n.t('trainer.trainer_gameFormSetting_dontSave') }}
                template(v-if="gameMode === 'free-game'")
                    cs-button.form__button(color="blue", @click='saveGameMode', :disabled="!params.selectedColors.length")
                        | {{ $i18n.t('trainer.trainer_gameFormSetting_saveGame') }}
                template(v-else='')
                    cs-button.form__button(color="blue", @click='saveGame', :disabled="!params.selectedColors.length")
                        | {{ $i18n.t('trainer.trainer_gameFormSetting_saveGame') }}
            .form__buttons(v-else)
                cs-button.form__button(@click='cancel', color="blue", pattern="primary-outline")
                    | {{ $i18n.t('trainer.trainer_gameFormSetting_dontSave') }}
                cs-button.form__button(color="blue", @click='saveGame', :disabled="!params.selectedColors.length")
                    | {{ $i18n.t('trainer.trainer_gameFormSetting_saveDifference') }}
</template>

<script>
    import GameFormMixin from '../../../utils/trainer/GameFormMixin';
    import TrainerGames from '../../../config/trainer';
    import { getInitParameters } from '../../../utils/trainer/getInitParameters';
    import { mapState } from 'vuex';
    import _ from 'lodash';
    import multiplayerFormMixin from '../../../utils/multiplayer/formMixin';
    import IntervalConfig from '../../trainer/configs/IntervalConfig';
    import KeenEyeConfig from '../../trainer/configs/KeenEyeConfig';

    export default {
        props: ['slug', 'courseType', 'id', 'usersIds', 'homeworkId', 'gameMode', 'gameId'],
        mixins: [GameFormMixin, multiplayerFormMixin],
        data() {
            return {
                keenEyeConfig: KeenEyeConfig,
                minutes: IntervalConfig.timeLimitForMemorize,
                seconds: IntervalConfig.secondsForAddAndSub,
                params: null,
            };
        },
        mounted() {
            if (this.gameMode === 'free-game') {
                let paramsArray = this.$store.state.trainer.paramsArray;
                if (paramsArray?.length && paramsArray[0] && paramsArray[0].slug === this.slug) {
                    this.paramsArray = _.cloneDeep(paramsArray);
                    this.$emit('playersNumber', this.paramsArray.length);
                    this.params = this.paramsArray[0];
                } else if (this.gamesParameters) {
                    this.$set(
                        this,
                        'params',
                        getInitParameters({
                            slug: this.slug,
                            parameters: {
                                default: _.cloneDeep(this.gamesParameters[this.slug]),
                            },
                        })
                    );
                } else {
                    this.params = _.cloneDeep(
                        TrainerGames.games.find(({ slug }) => slug === this.slug).parameters.default
                    );
                }
                this.isMentalArithmeticGame && (this.params.infinityAmountOfExamples = true);
            } else {
                this.setInitParams(this.getKey(this.usersIds));
            }
        },
        methods: {
            displaySpeedLabel(amount) {
                return amount === 0
                    ? this.$i18n.t('trainer.trainer_gameFormSettingValue_off')
                    : amount + ' ' + this.$i18n.t('trainer.trainer_gameFormSettingValue_seconds');
            },
            timeLimitLabel(amount) {
                return amount === 0
                    ? this.$i18n.t('trainer.trainer_gameFormSettingValue_off')
                    : amount + ' ' + this.$i18n.t('trainer.trainer_gameFormSettingValue_minutes');
            },
            colorLabel(color) {
                return this.$i18n.t('common.common_gameSetting_color' + `${color.name}`);
            },
            selectDisplaySpeed(speed) {
                this.params.displaySpeed = speed;
            },
            selectTimeLimit(limit) {
                this.params.timeLimit = limit;
            },
            selectTableSize(size) {
                this.params.difficult = size;
            },
        },
        computed: {
            ...mapState({
                homework: state => state.trainer.homework,
                gamesParameters: state => state.trainer.gamesParameters,
                user: state => state.auth.user,
            }),
        },
    };
</script>

<style lang="scss" scoped>
    .form__title {
        font-weight: 500;
        font-size: 22px;
        line-height: 26px;
        text-align: center;
        margin-bottom: 25px;
    }

    .form__body {
        width: 100%;
        box-sizing: border-box;
        @include media($lg) {
            padding: 0 4.5%;
        }
    }

    .form__field {
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
        @include media($md) {
            padding: 0 2.5px;
            flex-direction: row;
            align-items: center;
        }
        @include media($lg) {
            padding: 0 10%;
        }
        @include media($xlg) {
            padding: 0 13%;
        }
    }

    .form__field--column {
        flex-direction: column;
        margin-bottom: 20px;
        align-items: flex-start;
    }

    .form__input {
        margin-bottom: 0;
    }

    .form__label {
        min-width: 0;
        font-size: 16px;
        line-height: 19px;
        margin-right: 20px;
        margin-bottom: 0.5rem;
        @include media($md) {
            width: 8em;
            margin-bottom: 0;
            text-align: right;
        }
        @include media($xlg) {
            max-width: none;
            min-width: 13em;
        }
    }

    .form__label--left {
        min-width: 0;
        width: auto;
        margin-bottom: 20px;
    }

    .form__label--image {
        @include media($md) {
            padding-top: 2.5em;
        }
    }

    .form__field-body {
        flex-grow: 5;
    }

    .form__field-body--inline {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
    }

    .form__field-body--bold {
        font-weight: 500;
    }

    .numbers-checkbox:not(:last-child) {
        margin-right: 10px;
    }

    .form__buttons {
        margin-top: 30px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        @include media($md) {
            flex-direction: row;
        }
    }

    .form__button {
        margin: 10px 50px;
        @include media($md) {
            margin: 0 10px;
        }
    }

    .form-container__select-examples {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        @media screen and (min-width: 700px) {
            flex-direction: row;
            align-items: center;
        }
    }

    .form-container__select-infinity-mode {
        display: flex;
        align-items: center;
        margin-top: 20px;
        @media screen and (min-width: 700px) {
            margin-top: 0;
        }
    }

    .form-container__checkbox {
        margin-left: 30px;
    }

    .form-container__svg-icon {
        opacity: 0.7;
        margin: 0 10px 10px 0;
    }
</style>
